import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowDown, Clock, Lock, RotateCw } from 'lucide-react';
import { cn } from 'lib/utils';
import { activeLocale } from 'i18n';
import { Skeleton } from 'components/ui/skeleton';
import { ApiError } from 'utils/unwrapResponse';
import type { AdminAuditEvent } from 'api/adminAuditApi';
import { dayKey, numericDate } from 'utils/auditLabels';
import RetentionNotice from './RetentionNotice';
import TimelineItem from './TimelineItem';

// J11's page size, mirrored: the API returns `nextCursor`, never the batch size itself.
const PAGE_SIZE = 200;

/** Today's Paris day, one calendar day back — computed from the key, not `now - 24h`, so the
 * spring-forward gap cannot land it a day early. */
const parisYesterday = (): string => {
  const [y, m, d] = dayKey(new Date().toISOString()).split('-').map(Number);
  return dayKey(new Date(Date.UTC(y, m - 1, d - 1, 12)).toISOString());
};

/** "Aujourd'hui" / "Hier" / "Tuesday 22 September" — compared and built from Paris day keys only. */
const dayGroupLabel = (t: TFunction, key: string): string => {
  const today = dayKey(new Date().toISOString());
  if (key === today) return t('Today');
  if (key === parisYesterday()) return t('Yesterday');
  // Noon UTC on that calendar day: any runtime timezone reads the same date back.
  const label = new Intl.DateTimeFormat(activeLocale(), { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(
    new Date(`${key}T12:00:00Z`)
  );
  return label.charAt(0).toUpperCase() + label.slice(1);
};

interface AuditTimelineProps {
  events: AdminAuditEvent[];
  total?: number;
  since?: string | null;
  retentionMonths: number | null;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isFetchNextPageError?: boolean;
  onLoadMore?: () => void;
}

/**
 * The chronology for one entity (J12, reused by the global view's detail drawer in PR9) — props
 * only, so a caller owns the fetch and this just renders what it is handed: loading, error/denied,
 * empty, the day-grouped list (CA25) with pagination (CA14), and the retention footer. Pagination
 * props are optional (M4): a single already-loaded event just omits them.
 */
const AuditTimeline = ({
  events,
  total = events.length,
  since = null,
  retentionMonths,
  isLoading,
  isError,
  error = null,
  onRetry = () => {},
  hasNextPage = false,
  isFetchingNextPage = false,
  isFetchNextPageError = false,
  onLoadMore = () => {},
}: AuditTimelineProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex gap-2.5">
            <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
            <div className="flex flex-1 flex-col gap-1.5 pt-0.5">
              <Skeleton className="h-3.5 w-3/5" />
              <Skeleton className="h-3.5 w-2/5" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // A failed `fetchNextPage` keeps the pages already loaded (CA13): only an initial load with
  // nothing on screen yet earns the full-page error/denied state.
  if (isError && !isFetchNextPageError && events.length === 0) {
    const denied = error instanceof ApiError && error.status === 403; // CA24 — no retry, it will not clear itself.
    return (
      <div role="alert" className="flex flex-col items-center gap-2 px-4 py-10 text-center">
        {denied ? (
          <>
            <Lock className="h-9 w-9 text-muted-foreground" aria-hidden="true" />
            <p className="text-[13.5px] font-medium">{t('Access denied')}</p>
          </>
        ) : (
          <>
            <AlertCircle className="h-9 w-9 text-rose-500" aria-hidden="true" />
            <p className="text-[13.5px] font-medium">{t('Unable to load the history')}</p>
            <p className="max-w-[320px] text-[12.5px] text-muted-foreground">
              {t('The rest of the record stays available. Try again in a moment.')}
            </p>
            <button
              type="button"
              onClick={onRetry}
              className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-[12.5px] font-medium hover:bg-accent"
            >
              <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
              {t('Retry')}
            </button>
          </>
        )}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-accent" aria-hidden="true">
          <Clock className="h-5 w-5 text-muted-foreground" />
        </span>
        <p className="text-[13.5px] font-medium">
          {since ? t('No actions recorded since {{date}}', { date: numericDate(dayKey(since)) }) : t('No actions recorded yet')}
        </p>
        {since && (
          <p className="max-w-[320px] text-[12.5px] text-muted-foreground">
            {t('The journal went live on that date. Earlier changes are not tracked.')}
          </p>
        )}
        <RetentionNotice months={retentionMonths} />
      </div>
    );
  }

  // Consecutive events sharing a Paris day (CA25) become one group — the list already arrives
  // newest-first, so a group only ever needs to compare against the one still open.
  const groups: Array<[string, AdminAuditEvent[]]> = [];
  events.forEach((event) => {
    const key = dayKey(event.occurredAt);
    const current = groups[groups.length - 1];
    if (current && current[0] === key) current[1].push(event);
    else groups.push([key, [event]]);
  });

  return (
    <div className="flex flex-col gap-5">
      {groups.map(([key, dayEvents]) => (
        <div key={key}>
          <div className="mb-1.5 flex items-center gap-2 text-[12px] text-muted-foreground">
            <span className="font-medium">{dayGroupLabel(t, key)}</span>
            <span className="font-mono">{numericDate(key)}</span>
            <span className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>
          <ul>
            {dayEvents.map((event) => (
              <TimelineItem key={event.id} event={event} />
            ))}
          </ul>
        </div>
      ))}

      <div className="flex flex-col gap-3 border-t pt-3">
        {isFetchNextPageError ? (
          <span role="alert" className="inline-flex flex-wrap items-center gap-2 self-start text-[12.5px] text-rose-600 dark:text-rose-400">
            {t('An error occurred')}
            <button type="button" onClick={onLoadMore} className="inline-flex items-center gap-1.5 font-medium underline hover:no-underline">
              <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
              {t('Retry')}
            </button>
          </span>
        ) : (
          hasNextPage && (
            <button
              type="button"
              disabled={isFetchingNextPage}
              onClick={onLoadMore}
              className="inline-flex items-center justify-center gap-1.5 self-start rounded-md border border-input px-3 py-1.5 text-[12.5px] font-medium hover:bg-accent disabled:opacity-50"
            >
              <ArrowDown className={cn('h-3.5 w-3.5', isFetchingNextPage && 'animate-spin')} aria-hidden="true" />
              {t('Load {{count}} more', { count: Math.min(PAGE_SIZE, total - events.length) })}
            </button>
          )
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 text-[12px] text-muted-foreground">
          <span>{t('{{shown}} shown out of {{total}}', { shown: events.length, total })}</span>
          <RetentionNotice months={retentionMonths} />
        </div>
      </div>
    </div>
  );
};

export default AuditTimeline;
